import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { heroSlides as mockSlides } from '../mock/mock';
import { imgProps, preloadHref } from '../lib/imgSrc';
import { Helmet } from 'react-helmet-async';
import { useContent } from '../context/ContentContext';
import { fetchHeroSlides } from '../lib/api';

const Hero = () => {
  const [idx, setIdx] = useState(0);
  const [apiSlides, setApiSlides] = useState(null); // null = still loading, [] = loaded empty
  const { get } = useContent();

  useEffect(() => {
    fetchHeroSlides().then(list => setApiSlides(Array.isArray(list) ? list : [])).catch(() => setApiSlides([]));
  }, []);

  // Use admin-managed slides when available. Fallback to mock ONLY on cold start (before API resolves).
  const slides = useMemo(() => {
    if (apiSlides === null) return mockSlides;
    if (apiSlides.length > 0) return apiSlides;
    return mockSlides;
  }, [apiSlides]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  useEffect(() => { if (idx >= slides.length) setIdx(0); }, [slides.length, idx]);

  const raw = slides[idx] || slides[0] || mockSlides[0];
  const currentImage = raw?.image || '';
  // For the FIRST admin-managed slide, still respect the Design tab text overrides (backwards compat).
  const slide = idx === 0 ? {
    ...raw,
    heading: get('home.hero.title', raw?.heading),
    accent: get('home.hero.accent', raw?.accent),
    subheading: get('home.hero.subtitle', raw?.subheading),
    cta: get('home.hero.cta.primary', raw?.cta),
    image: currentImage,
  } : { ...raw, image: currentImage };
  const secondaryCta = get('home.hero.cta.secondary', 'Our Story');
  const eyebrow = get('home.hero.eyebrow', 'Since December 2018');
  const stat1v = get('home.hero.stat1.value', '10K+');
  const stat1l = get('home.hero.stat1.label', 'Happy Customers');
  const stat2v = get('home.hero.stat2.value', '180+');
  const stat2l = get('home.hero.stat2.label', 'Unique Designs');
  const stat3v = get('home.hero.stat3.value', '4.9★');
  const stat3l = get('home.hero.stat3.label', 'Google Rating');

  return (
    <section className="relative overflow-hidden gradient-cream">
      <Helmet>
        {slides[0]?.image && <link rel="preload" as="image" href={preloadHref(slides[0].image, 900)} />}
      </Helmet>
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(232,136,100,0.15), transparent 50%), radial-gradient(circle at 80% 70%, rgba(245,208,206,0.4), transparent 50%)" }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left content */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#E7DCC7] rounded-full px-4 py-1.5 mb-6 animate-fade-up">
            <Sparkles className="w-3.5 h-3.5 text-[#8A6A22]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#213A60]">{eyebrow}</span>
          </div>

          <h1 key={slide.id} className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-[#213A60] animate-fade-up">
            {slide.heading}<br />
            <span className="italic text-[#8A6A22]">{slide.accent}</span>
          </h1>

          <p key={slide.id + '-p'} className="mt-6 text-neutral-700 text-lg max-w-lg leading-relaxed animate-fade-up">
            {slide.subheading}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up">
            <Link to={slide.href} className="group inline-flex items-center gap-3 bg-[#213A60] hover:bg-[#D4A63A] text-white px-7 py-4 rounded-full text-xs uppercase tracking-[0.2em] transition-colors duration-500">
              {slide.cta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/about" className="text-xs uppercase tracking-[0.2em] link-underline text-[#213A60]">{secondaryCta}</Link>
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap gap-6 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
            <div><span className="text-[#8A6A22] font-semibold text-lg block">{stat1v}</span>{stat1l}</div>
            <div><span className="text-[#8A6A22] font-semibold text-lg block">{stat2v}</span>{stat2l}</div>
            <div><span className="text-[#8A6A22] font-semibold text-lg block">{stat3v}</span>{stat3l}</div>
          </div>
        </div>

        {/* Right visual */}
        <div className="relative h-[420px] md:h-[560px]">
          <div className="absolute inset-0 flex">
            {slides.map((s, i) => (
              <div key={s.id} className={`absolute inset-0 transition-all duration-1000 ${i === idx ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <div className="relative w-full h-full">
                  <div className="absolute -top-8 -right-6 w-56 h-56 rounded-full bg-[#EFE7D5] blur-2xl opacity-70" />
                  <div className="absolute -bottom-6 -left-6 w-64 h-64 rounded-full bg-[#E2BB4B]/30 blur-2xl" />
                  <img {...imgProps(s.image, { width: 900, height: 900, sizes: '(max-width: 768px) 100vw, 50vw', eager: i === 0 })} alt={s.accent} className="relative w-full h-full object-cover rounded-[24px] shadow-2xl border border-white/60" />
                  <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur rounded-lg px-4 py-3 shadow-lg">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A6A22]">Featured</div>
                    <div className="font-serif-display text-xl text-[#213A60] italic">{s.accent}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Slide indicators */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`h-1.5 transition-all duration-500 rounded-full ${i === idx ? 'w-8 bg-[#D4A63A]' : 'w-1.5 bg-[#D4A63A]/30'}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
